import { AGENCY_POLICY, applyAgencyPolicy } from "./agency/index.ts";
import { type Carrier, isCarrier } from "./carriers/carrier.ts";
import { NATIONAL_GENERAL } from "./carriers/national-general.ts";
import { STEADILY } from "./carriers/steadily.ts";
import { businessName, effectiveDate, prefilled, propertyAddress } from "./industry.ts";

export { type Carrier, carrierKey } from "./carriers/carrier.ts";

/**
 * The carrier directory (docs/carrier-runs.md#carrier-directory): where each carrier's portal is,
 * so `start_run` can open a run's browser there without the producer giving the URL. A URL passed
 * to `start_run` always wins, since new carriers come all the time.
 *
 * A carrier's requirements are built from three layers (docs/self-improvement.md#five-layers): what
 * every quote needs (`industry.ts`), what the carrier asks and assumes (`carriers/`), and the
 * agency's own defaults (`agency/`), which take the place of the carrier's.
 */

/**
 * The carriers we quote through a carrier skill, as carriers: the same for every agency. Add each
 * real carrier in `carriers/`: the name producers say, its aliases (short names, former names), the
 * page its agent portal starts at (usually the sign-in page), and its requirements.
 */
export const CARRIERS: readonly Carrier[] = [NATIONAL_GENERAL, STEADILY];

/** The agency's carriers: the carriers with the agency's policy applied. */
export const AGENCY_CARRIERS: readonly Carrier[] = applyAgencyPolicy(CARRIERS, AGENCY_POLICY);

/**
 * The capture lab's mock carriers, which the local end-to-end run uses. They listen on consecutive
 * ports from the first one (Acme Mutual's), so their URLs come from configuration.
 */
export const MOCK_CARRIERS = [
  {
    name: "Acme Mutual",
    aliases: ["Acme"],
    portOffset: 0,
    lines: "small business: general liability and business property (mock)",
    requirements: [
      businessName,
      propertyAddress,
      {
        key: "yearBuilt",
        label: "Year built",
        from: "property",
        need: "provide",
        ask: "The year the building was built",
      },
      effectiveDate,
    ],
  },
  {
    name: "Bison Insurance",
    aliases: ["Bison"],
    portOffset: 1,
    lines: "small business: general liability and business property (mock)",
    requirements: [businessName, propertyAddress, ...prefilled("roofType")],
  },
] as const satisfies readonly (Omit<Carrier, "portalUrl"> & { portOffset: number })[];

/** The mock carriers on `localhost`, from Acme Mutual's port. */
export function mockCarriers(firstPort: number): Carrier[] {
  return MOCK_CARRIERS.map((c) => ({
    name: c.name,
    aliases: c.aliases,
    portalUrl: `http://localhost:${firstPort + c.portOffset}/`,
    lines: c.lines,
    requirements: c.requirements,
  }));
}

export type CarrierDirectoryOptions = {
  /** Acme Mutual's port when the mock carriers run on this machine; without it they are left out. */
  mockCarriersPort?: number;
};

/** The directory a daemon uses: the agency's carriers, and the mock carriers when configured. */
export function carrierDirectory(options: CarrierDirectoryOptions = {}): Carrier[] {
  const port = options.mockCarriersPort;
  return [...AGENCY_CARRIERS, ...(port !== undefined ? mockCarriers(port) : [])];
}

/** The directory's carrier with this name or alias, by carrier key; the first one if several. */
export function findCarrier(directory: readonly Carrier[], name: string): Carrier | undefined {
  return directory.find((c) => isCarrier(c, name));
}

/**
 * The agency's CRMs, which a producer pulls a client's loan from (the crm-pull skill), which a run names as its
 * carrier. A run at one reads the loan and prices nothing; each Arive company has its own host, so
 * their addresses are the producers' own (docs/decisions.md, 2026-10-23).
 */
export const CRMS: readonly Pick<Carrier, "name" | "aliases">[] = AGENCY_POLICY.crms;

/** Whether a run's carrier is a CRM rather than an insurance carrier. */
export function isCrm(name: string): boolean {
  return CRMS.some((crm) => isCarrier(crm, name));
}

/**
 * The run that reads a county's public record page or Zillow when the property lookup lacks a
 * fact (the property-lookup skill). Like a CRM run it prices nothing; it opens the page it is
 * given, so its address is never remembered (docs/decisions.md, 2026-10-26).
 */
export const PROPERTY_RECORDS: Pick<Carrier, "name" | "aliases"> = {
  name: "Property records",
  aliases: ["Property lookup"],
};

/** Whether a run's carrier is the property records run rather than an insurance carrier. */
export function isLookup(name: string): boolean {
  return isCarrier(PROPERTY_RECORDS, name);
}

/** What a run is for: pricing at a carrier, reading a loan in a CRM, or reading property records. */
export type RunKind = "carrier" | "crm" | "lookup";

export function runKind(name: string): RunKind {
  if (isCrm(name)) return "crm";
  if (isLookup(name)) return "lookup";
  return "carrier";
}
